import { useState } from "react";
import axios from "axios";
import { Mail, MessageCircle, Clock, CircleCheck, ShieldCheck, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import { FadeIn, MaskedLines, Chapter } from "@/components/motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { BRAND, TREATMENTS } from "@/data/content";

const API = "/api"; // same-origin: ingress routes /api to the backend

const COUNTRIES = [
  "United States",
  "Canada",
  "United Kingdom",
  "Germany",
  "France",
  "Italy",
  "Spain",
  "Australia",
  "UAE",
  "Saudi Arabia",
  "Nigeria",
  "Kenya",
  "South Africa",
  "Other",
];

const INITIAL = {
  full_name: "",
  email: "",
  phone: "",
  country: "",
  treatment: "",
  message: "",
};

export default function Contact() {
  const [form, setForm] = useState(INITIAL);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.country) {
      toast.error("Please select your country");
      return;
    }
    if (!form.treatment) {
      toast.error("Please select a treatment area", {
        description: "Choose the closest option — 'Not sure yet' is fine too.",
      });
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success("Inquiry received", {
        description: "A GetWellSoon coordinator will contact you within 24 hours.",
      });
    } catch (err) {
      toast.error("Something went wrong", {
        description: "Please try again, or reach us directly on WhatsApp.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <SEO
        title="Contact a Care Coordinator | GetWellSoon"
        description="Speak with a GetWellSoon care coordinator about treatment options in India. Share your reports and receive hospital options and indicative estimates."
      />

      <section className="bg-pearl py-20 md:py-28" data-testid="contact-page">
        <div className="mx-auto grid max-w-7xl gap-16 px-5 md:px-8 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Chapter index="01" label="Contact" />
            <MaskedLines
              delay={0.1}
              className="mt-8 font-serif text-4xl leading-[1.06] tracking-tight text-ink sm:text-5xl"
              lines={[
                "Speak with a",
                <span key="l2">
                  care <span className="italic text-brand">coordinator</span>.
                </span>,
              ]}
            />
            <FadeIn delay={0.4}>
              <p className="mt-6 max-w-md text-base leading-relaxed text-muted-foreground">
                Tell us a little about your situation. A coordinator will review
                your message personally and respond with clear next steps — no
                obligations, no pressure.
              </p>
            </FadeIn>

            <FadeIn delay={0.5} className="mt-10 space-y-5">
              <a
                href={`mailto:${BRAND.email}`}
                data-testid="contact-email-link"
                className="flex items-center gap-4 text-sm font-semibold text-ink transition-colors hover:text-brand"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-brand-light text-brand">
                  <Mail className="h-5 w-5" />
                </span>
                {BRAND.email}
              </a>
              <a
                href={BRAND.whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-link"
                className="flex items-center gap-4 text-sm font-semibold text-ink transition-colors hover:text-brand"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-sage-light text-sage-dark">
                  <MessageCircle className="h-5 w-5" />
                </span>
                WhatsApp: {BRAND.whatsappDisplay}
              </a>
              <div className="flex items-center gap-4 text-sm font-semibold text-ink">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-brand-light text-brand">
                  <Clock className="h-5 w-5" />
                </span>
                Replies within one business day
              </div>
            </FadeIn>

            <FadeIn delay={0.6} className="mt-10 space-y-3 border-t border-border pt-8">
              {[
                "Your reports stay private and are shared only with hospitals you approve",
                "Estimates come from hospitals, based on your actual records",
                "You decide if and when to proceed — always",
              ].map((t) => (
                <p key={t} className="flex items-start gap-3 text-sm leading-relaxed text-ink/80">
                  <CircleCheck className="mt-0.5 h-4 w-4 shrink-0 text-sage-dark" />
                  {t}
                </p>
              ))}
            </FadeIn>

            <FadeIn delay={0.7} className="mt-10 hidden lg:block">
              <div className="overflow-hidden rounded-[2rem] rounded-bl-[4rem] border border-border">
                <img
                  src="/images/coordinator-portrait.jpg"
                  alt="A member of the patient coordination team"
                  className="h-56 w-full object-cover"
                  data-testid="contact-image"
                />
              </div>
            </FadeIn>
          </div>

          <div className="lg:col-span-7">
            <FadeIn delay={0.2}>
              <div className="rounded-[2rem] border border-border bg-white p-6 md:p-10">
                {sent ? (
                  <div className="flex flex-col items-start gap-5 py-10" data-testid="contact-success-panel">
                    <span className="flex h-14 w-14 items-center justify-center rounded-full bg-sage-light text-sage-dark">
                      <CheckCircle2 className="h-7 w-7" />
                    </span>
                    <h2 className="font-serif text-3xl tracking-tight text-ink">
                      Thank you. Your inquiry has been received.
                    </h2>
                    <p className="max-w-md text-base leading-relaxed text-muted-foreground">
                      A GetWellSoon coordinator will contact you within 24
                      hours. If your situation is urgent, message us on
                      WhatsApp for a faster response.
                    </p>
                    <Button
                      variant="outline"
                      className="mt-2 rounded-full border-ink/15 px-6 font-semibold hover:border-brand hover:text-brand"
                      onClick={() => {
                        setForm(INITIAL);
                        setSent(false);
                      }}
                      data-testid="contact-send-another-button"
                    >
                      Send another inquiry
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={submit} data-testid="contact-form" noValidate={false}>
                    <div className="grid gap-6 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="full_name">Full name</Label>
                        <Input
                          id="full_name"
                          data-testid="contact-full-name-input"
                          required
                          minLength={2}
                          placeholder="Jane Smith"
                          value={form.full_name}
                          onChange={set("full_name")}
                          className="h-12 bg-gray-50"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">Email</Label>
                        <Input
                          id="email"
                          type="email"
                          data-testid="contact-email-input"
                          required
                          placeholder="jane@example.com"
                          value={form.email}
                          onChange={set("email")}
                          className="h-12 bg-gray-50"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="phone">Phone / WhatsApp</Label>
                        <Input
                          id="phone"
                          type="tel"
                          data-testid="contact-phone-input"
                          required
                          minLength={5}
                          placeholder="+1 555 000 0000"
                          value={form.phone}
                          onChange={set("phone")}
                          className="h-12 bg-gray-50"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Country</Label>
                        <Select
                          value={form.country}
                          onValueChange={(v) =>
                            setForm((f) => ({ ...f, country: v }))
                          }
                        >
                          <SelectTrigger
                            data-testid="contact-country-select"
                            className="h-12 bg-gray-50"
                          >
                            <SelectValue placeholder="Select your country" />
                          </SelectTrigger>
                          <SelectContent className="bg-white">
                            {COUNTRIES.map((c) => (
                              <SelectItem key={c} value={c}>
                                {c}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2 sm:col-span-2">
                        <Label>Treatment inquiry</Label>
                        <Select
                          value={form.treatment}
                          onValueChange={(v) => setForm((f) => ({ ...f, treatment: v }))}
                        >
                          <SelectTrigger
                            data-testid="contact-treatment-select"
                            className="h-12 bg-gray-50"
                          >
                            <SelectValue placeholder="Select a treatment area" />
                          </SelectTrigger>
                          <SelectContent className="bg-white">
                            {TREATMENTS.map((t) => (
                              <SelectItem key={t.slug} value={t.name}>
                                {t.name}
                              </SelectItem>
                            ))}
                            <SelectItem value="Other / Not sure yet">
                              Other / Not sure yet
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2 sm:col-span-2">
                        <Label htmlFor="message">Message</Label>
                        <Textarea
                          id="message"
                          data-testid="contact-message-input"
                          required
                          minLength={10}
                          rows={5}
                          placeholder="Tell us briefly about the patient's condition and what you are looking for. Please do not include sensitive details here — reports can be shared securely later."
                          value={form.message}
                          onChange={set("message")}
                          className="bg-gray-50"
                        />
                      </div>
                    </div>

                    <Button
                      type="submit"
                      disabled={sending}
                      data-testid="contact-submit-button"
                      className="mt-8 h-12 w-full rounded-full bg-brand font-semibold text-white transition-colors duration-300 hover:bg-brand-dark sm:w-auto sm:px-10"
                    >
                      {sending ? "Sending..." : "Request a Treatment Estimate"}
                    </Button>
                    <p className="mt-5 flex items-start gap-2.5 text-xs leading-relaxed text-muted-foreground">
                      <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-sage-dark" />
                      GetWellSoon provides coordination services only. Submitting
                      this form does not create a patient-provider relationship,
                      and no medical advice is given through this website.
                    </p>
                  </form>
                )}
              </div>
            </FadeIn>
          </div>
        </div>
      </section>
    </>
  );
}
